import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE } from '../config';
import { adminJson } from '../utils/adminAuth';

const ENUMS = {
  scope_type: ['single_dealer', 'multi_dealer', 'all_dealers'],
  export_format: ['cdk', 'homenet', 'vauto', 'nexteppe', 'xml', 'csv'],
  delivery_method: ['ftp', 'sftp', 'http', 'email'],
  sync_type: ['full', 'incremental'],
  schedule_type: ['hourly', 'daily', 'weekly', 'monthly'],
};
// [key, label, kind]  kind: text|number|select|check|date
const SECTIONS = {
  Basics: [['feed_name', 'Feed name', 'text'], ['scope_type', 'Scope', 'select'], ['is_active', 'Active', 'check']],
  Schedule: [['schedule_type', 'Schedule', 'select'], ['schedule_time', 'Time (HH:MM:SS)', 'text'], ['schedule_day', 'Day (wk/mo)', 'number'], ['schedule_interval', 'Interval (hrs)', 'number']],
  Delivery: [['export_format', 'Format', 'select'], ['delivery_method', 'Method', 'select'], ['sync_type', 'Sync', 'select'], ['endpoint_url', 'Endpoint URL', 'text'], ['username', 'Username', 'text'], ['password', 'Password', 'text'], ['api_key', 'API key', 'text'], ['remote_path', 'Remote path', 'text']],
  Billing: [['monthly_fee', 'Monthly fee', 'number'], ['per_vehicle_fee', 'Per-vehicle fee', 'number'], ['contract_start_date', 'Contract start', 'date'], ['contract_end_date', 'Contract end', 'date'], ['account_rep_name', 'Rep name', 'text'], ['account_rep_email', 'Rep email', 'text'], ['account_rep_phone', 'Rep phone', 'text'], ['notes', 'Notes', 'text']],
};
const defaults = {
  feed_name: '', partner_id: '', owner_dealership_id: '', scope_type: 'single_dealer', is_active: true,
  schedule_type: 'daily', schedule_time: '06:00:00', schedule_day: '', schedule_interval: 8,
  export_format: 'cdk', delivery_method: 'ftp', sync_type: 'full', endpoint_url: '', username: '',
  password: '', api_key: '', remote_path: '', monthly_fee: 0, per_vehicle_fee: 0,
  contract_start_date: '', contract_end_date: '', account_rep_name: '', account_rep_email: '', account_rep_phone: '', notes: '',
};
const NUMS = new Set(['schedule_day', 'schedule_interval', 'monthly_fee', 'per_vehicle_fee']);

export default function AdminFeeds() {
  const navigate = useNavigate();
  const [feeds, setFeeds] = useState([]);
  const [partners, setPartners] = useState([]);
  const [dealers, setDealers] = useState([]);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [editing, setEditing] = useState(null);   // null | 'new' | feed
  const [form, setForm] = useState(defaults);

  const authHeader = useCallback(() => {
    const token = localStorage.getItem('adminAuth');
    if (!token) { navigate('/admin/login'); return null; }
    return { Authorization: `Bearer ${token}` };
  }, [navigate]);
  const flash = (m) => { setMessage(m); setTimeout(() => setMessage(''), 2800); };

  const loadFeeds = useCallback(() => {
    const h = authHeader();
    if (!h) return;
    setLoading(true);
    fetch(`${API_BASE}/admin/syndication/feeds?include_inactive=${includeInactive}`, { headers: h })
      .then(adminJson).then(d => setFeeds(d.feeds || []))
      .catch(() => setError('Failed to load feeds')).finally(() => setLoading(false));
  }, [authHeader, includeInactive]);

  useEffect(() => { loadFeeds(); }, [loadFeeds]);
  useEffect(() => {
    const h = authHeader();
    if (!h) return;
    fetch(`${API_BASE}/admin/syndication/partners`, { headers: h }).then(adminJson)
      .then(d => setPartners(d.partners || (Array.isArray(d) ? d : []))).catch(() => {});
    fetch(`${API_BASE}/admin/dealers?limit=500`, { headers: h }).then(adminJson)
      .then(d => setDealers((Array.isArray(d) ? d : (d.dealers || d.dealerships || [])))).catch(() => {});
  }, [authHeader]);

  const openAdd = () => { setForm(defaults); setEditing('new'); };
  const openEdit = (f) => {
    const next = { ...defaults };
    Object.keys(defaults).forEach(k => { if (f[k] !== undefined && f[k] !== null) next[k] = f[k]; });
    next.partner_id = f.partner_id || ''; next.password = '';
    setForm(next); setEditing(f);
  };

  const save = async () => {
    const h = authHeader();
    if (!h) return;
    if (!form.feed_name.trim() || !form.partner_id) { setError('Feed name and partner are required'); return; }
    const body = { ...form, partner_id: Number(form.partner_id) };
    NUMS.forEach(k => { body[k] = body[k] === '' ? null : Number(body[k]); });
    ['owner_dealership_id', 'schedule_day', 'contract_start_date', 'contract_end_date'].forEach(k => { if (body[k] === '') body[k] = null; });
    if (!body.password) delete body.password;
    try {
      const isNew = editing === 'new';
      const res = await fetch(isNew ? `${API_BASE}/admin/syndication/feeds` : `${API_BASE}/admin/syndication/feeds/${editing.id}`, {
        method: isNew ? 'POST' : 'PUT', headers: { ...h, 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); setError(e.detail || 'Save failed'); return; }
      setEditing(null); flash(isNew ? 'Feed created' : 'Feed updated'); loadFeeds();
    } catch (e) { setError('Save failed'); }
  };

  const act = async (f, path, method, okMsg, body) => {
    const h = authHeader();
    if (!h) return;
    const res = await fetch(`${API_BASE}/admin/syndication/feeds/${f.id}${path}`, {
      method, headers: body ? { ...h, 'Content-Type': 'application/json' } : h, body: body ? JSON.stringify(body) : undefined,
    });
    if (res.ok) { flash(okMsg); loadFeeds(); }
    else { const e = await res.json().catch(() => ({})); setError(e.detail || 'Action failed'); }
  };

  const setF = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  return (
    <div style={{ padding: 20, maxWidth: 1200, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2>Syndication Feeds</h2>
      </div>
      {error && <div style={{ color: '#dc2626', marginBottom: 8 }}>{error} <button onClick={() => setError('')} style={linkBtn}>dismiss</button></div>}
      {message && <div style={{ color: '#059669', marginBottom: 8 }}>{message}</div>}

      <div style={{ display: 'flex', gap: 12, alignItems: 'center', margin: '12px 0', flexWrap: 'wrap' }}>
        <button onClick={openAdd} style={primaryBtn}>+ Add Feed</button>
        <label style={{ fontSize: 13, color: '#6b7280', display: 'flex', gap: 6, alignItems: 'center' }}>
          <input type="checkbox" checked={includeInactive} onChange={e => setIncludeInactive(e.target.checked)} /> Show inactive
        </label>
        <span style={{ color: '#6b7280', fontSize: 13 }}>{feeds.length} feed(s)</span>
      </div>

      {loading ? <div>Loading…</div> : (
        <table style={table}>
          <thead><tr>{['Feed', 'Partner', 'Dealer', 'Schedule', 'Format', 'Status', 'Actions'].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
          <tbody>
            {feeds.map(f => (
              <tr key={f.id} style={{ borderBottom: '1px solid #eef2f7' }}>
                <td style={td}>{f.feed_name}<div style={{ fontSize: 12, color: '#94a3b8' }}>{f.feed_code}</div></td>
                <td style={td}>{f.partner_name}</td>
                <td style={td}>{f.dealership_name || f.owner_dealership_id || '—'}</td>
                <td style={td}>{f.schedule_type}{f.schedule_time ? ` @ ${String(f.schedule_time).slice(0, 5)}` : ''}</td>
                <td style={td}>{f.export_format}/{f.delivery_method}</td>
                <td style={td}>
                  {!f.is_active ? <span style={{ color: '#94a3b8' }}>inactive</span>
                    : f.is_paused ? <span style={{ color: '#d97706' }}>⏸ paused</span>
                      : <span style={{ color: '#059669' }}>active</span>}
                </td>
                <td style={{ ...td, whiteSpace: 'nowrap', fontSize: 13 }}>
                  <button onClick={() => openEdit(f)} style={linkBtn}>Edit</button>{' · '}
                  <button onClick={() => act(f, '/toggle-pause', 'POST', f.is_paused ? 'Resumed' : 'Paused', { pause_reason: f.is_paused ? '' : 'paused via admin' })} style={linkBtn}>{f.is_paused ? 'Resume' : 'Pause'}</button>{' · '}
                  <button onClick={() => window.confirm(`Run "${f.feed_name}" now?`) && act(f, '/run-now', 'POST', 'Feed run triggered')} style={linkBtn}>Run now</button>{' · '}
                  <button onClick={() => window.confirm(`Delete feed "${f.feed_name}"?`) && act(f, '', 'DELETE', 'Feed deleted')} style={{ ...linkBtn, color: '#dc2626' }}>Delete</button>
                </td>
              </tr>
            ))}
            {!feeds.length && <tr><td colSpan={7} style={{ ...td, color: '#6b7280' }}>No feeds.</td></tr>}
          </tbody>
        </table>
      )}

      {editing && (
        <div style={overlay} onClick={() => setEditing(null)}>
          <div style={{ ...modal, width: 620 }} onClick={e => e.stopPropagation()}>
            <h3>{editing === 'new' ? 'Add Feed' : `Edit ${editing.feed_name}`}</h3>
            <div style={{ maxHeight: '64vh', overflowY: 'auto', paddingRight: 4 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <label style={lbl}>Partner *
                  <select value={form.partner_id} onChange={e => setF('partner_id', e.target.value)} style={inp}>
                    <option value="">— select —</option>
                    {partners.map(p => <option key={p.id} value={p.id}>{p.partner_name || p.name}</option>)}
                  </select>
                </label>
                <label style={lbl}>Dealer (owner)
                  <select value={form.owner_dealership_id || ''} onChange={e => setF('owner_dealership_id', e.target.value)} style={inp}>
                    <option value="">— none / all —</option>
                    {dealers.map(d => <option key={d.id} value={d.dealer_code}>{d.dealership_name || d.name}</option>)}
                  </select>
                </label>
              </div>
              {Object.entries(SECTIONS).map(([section, fields]) => (
                <div key={section}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', margin: '12px 0 6px' }}>{section}</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    {fields.map(([k, label, kind]) => kind === 'check' ? (
                      <label key={k} style={{ ...lbl, display: 'flex', alignItems: 'center', gap: 6, marginTop: 18 }}>
                        <input type="checkbox" checked={!!form[k]} onChange={e => setF(k, e.target.checked)} /> {label}
                      </label>
                    ) : (
                      <label key={k} style={lbl}>{label}
                        {kind === 'select'
                          ? <select value={form[k]} onChange={e => setF(k, e.target.value)} style={inp}>{ENUMS[k].map(o => <option key={o} value={o}>{o}</option>)}</select>
                          : <input type={kind === 'number' ? 'number' : 'text'} value={form[k] ?? ''} onChange={e => setF(k, e.target.value)} style={inp} placeholder={kind === 'date' ? 'YYYY-MM-DD' : ''} />}
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
              <button onClick={save} style={primaryBtn}>Save</button>
              <button onClick={() => setEditing(null)} style={btn}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const btn = { padding: '8px 14px', border: '1px solid #d1d5db', borderRadius: 6, background: '#fff', cursor: 'pointer' };
const primaryBtn = { ...btn, background: '#0078D4', color: '#fff', border: 'none' };
const linkBtn = { background: 'none', border: 'none', color: '#0078D4', cursor: 'pointer', padding: 0 };
const table = { width: '100%', borderCollapse: 'collapse', fontSize: 14 };
const th = { textAlign: 'left', padding: '8px 10px', borderBottom: '2px solid #e5e7eb', color: '#6b7280', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.04em' };
const td = { padding: '8px 10px', verticalAlign: 'top' };
const overlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 };
const modal = { background: '#fff', borderRadius: 10, padding: 22, maxWidth: '94vw', boxShadow: '0 10px 40px rgba(0,0,0,0.2)' };
const lbl = { display: 'block', fontSize: 12, color: '#6b7280', marginBottom: 4 };
const inp = { width: '100%', padding: 8, border: '1px solid #d1d5db', borderRadius: 6, boxSizing: 'border-box', marginTop: 2 };
